// Cliente HTTP compartido para todos los módulos que hablan con el backend
// ecommercejwt (productos, carrito, auth, direcciones). Centraliza:
//
// 1. Elegir proxy (same-origin /api/...) vs. backend directo según el
//    protocolo real de la página, en RUNTIME (no en build time / NODE_ENV).
//    El mismo bundle de producción se sirve tanto en https://miestampita.com
//    (donde hace falta el proxy por mixed content: el backend es http puro)
//    como localmente por http:// (dev server y el propio pipeline de
//    prerender), donde no hay ese problema y conviene pegarle directo.
// 2. El estilo `?route=/lo-que-sea` que este backend necesita al pegarle
//    directo: su .htaccess solo tiene `ErrorDocument 404 /index.php` como
//    mecanismo de ruteo (sin RewriteRule), lo que pierde el método/body en
//    POST y bloquea PUT/DELETE a nivel de Apache para cualquier ruta
//    "limpia". `?route=` apunta a un recurso real (index.php) y evita todo
//    eso -- confirmado probando contra el backend real. El query se manda
//    a `/index.php` explícito, no a `/`: el `.htaccess` tiene
//    `DirectoryIndex index.html index.php` (para el admin, que también
//    vive en este mismo host), así que `/` sirve el index.html del admin
//    en vez de correr el router de PHP -- confirmado con curl.
// 3. Ignorar el status HTTP: el backend devuelve 404 en TODAS las
//    respuestas de rutas "limpias", incluso exitosas. La única señal
//    confiable de error es una clave `error` en el body.
const DIRECT_BACKEND_ORIGIN = 'http://s152839045.onlinehome.us';

const shouldUseProxy = () => typeof window !== 'undefined' && window.location.protocol === 'https:';

const buildUrl = (path, params = {}) => {
  const query = new URLSearchParams(params);

  if (shouldUseProxy()) {
    const queryString = query.toString();
    return `/api${path}${queryString ? `?${queryString}` : ''}`;
  }

  query.set('route', path);
  return `${DIRECT_BACKEND_ORIGIN}/index.php?${query.toString()}`;
};

const parseResponse = async (response) => {
  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error('Respuesta inválida del servidor.');
  }

  if (data && !Array.isArray(data) && data.error) {
    throw new Error(data.error);
  }

  return data;
};

// `params` se manda como query string (identidad de invitado/JWT, filtros
// como `categoria`, etc.); el resto de `options` pasa tal cual a fetch().
export const request = async (path, { params, ...options } = {}) => {
  const response = await fetch(buildUrl(path, params), {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  return parseResponse(response);
};

// Las imágenes de producto también viven en el backend http-only -- mismo
// problema de mixed content que las llamadas a la API, pero el navegador
// las sube solo a https automáticamente (en vez de bloquearlas directo),
// lo que termina en ERR_SSL_PROTOCOL_ERROR porque ese host no habla TLS.
// Se reescriben al mismo proxy de Vercel (api/proxy-images.js, vía el
// rewrite de vercel.json), sólo cuando hace falta.
//
// OJO: acá NO se puede reusar shouldUseProxy() tal cual. El pre-render
// (scripts/prerender.js) visita un server local por http://, así que
// shouldUseProxy() da false ahí -- el HTML estático que efectivamente
// ven los crawlers/auditorías de SEO queda con las URLs http:// del
// backend sin proxear (nunca llegan a ejecutar el JS de un browser real
// que las corrige después de hidratar). prerender.js setea
// window.__FORCE_IMAGE_PROXY__ antes de cargar la página para forzar acá
// el mismo resultado que va a tener el sitio real en producción, sin
// tocar shouldUseProxy(): el ruteo de /api/... sí tiene que seguir
// pegándole directo al backend durante el pre-render, porque la función
// serverless del proxy no existe fuera de Vercel (el server local del
// pre-render es un `serve -s build` estático, sin funciones).
const IMAGE_PATH_PREFIX = '/images/';

const shouldProxyImages = () =>
  shouldUseProxy() || (typeof window !== 'undefined' && window.__FORCE_IMAGE_PROXY__ === true);

export const buildImageUrl = (rawUrl) => {
  if (!rawUrl || !shouldProxyImages()) return rawUrl;

  try {
    const { pathname } = new URL(rawUrl, DIRECT_BACKEND_ORIGIN);
    if (!pathname.startsWith(IMAGE_PATH_PREFIX)) return rawUrl;
    return `/api/images/${pathname.slice(IMAGE_PATH_PREFIX.length)}`;
  } catch {
    return rawUrl;
  }
};
